import type { AccountStatus, UserRole } from './context';

/**
 * 상태 파생에 필요한 사용자 필드 부분집합.
 *
 * 서버(UserRecord, Date)와 클라이언트(tRPC 직렬화 결과, ISO 문자열)가 같은 함수를
 * 쓰도록 Date | string 을 모두 허용한다 — 파생 판정은 존재 여부만 본다.
 */
export interface AccountStatusSource {
  readonly role: UserRole;
  readonly emailVerifiedAt: Date | string | null;
  readonly identityVerifiedAt: Date | string | null;
  readonly deletedAt: Date | string | null;
  readonly blockedAt: Date | string | null;
}

/**
 * 타임스탬프에서 계정 상태를 파생한다. (상태 컬럼을 따로 두지 않는다)
 *
 * 우선순위 순 판정: DELETED > BLOCKED > PENDING_EMAIL > PENDING_IDENTITY > ACTIVE
 *
 * 서버와 클라이언트가 공유하는 단일 구현이다 — 판정 규칙이 두 곳으로 갈라지면
 * 컴파일러가 불일치를 잡지 못하고 온보딩 라우팅이 런타임에만 어긋난다.
 * 서버에서는 파생 상태가 액세스 토큰 페이로드에 실려 요청마다 DB 조회를 없앤다.
 *
 * `identityRequired` 는 서버 설정(IDENTITY_PROVIDER)에서 온다 — `false`(disabled)면
 * PENDING_IDENTITY 판정만 건너뛴다. 옵션을 필수로 둬서 빠뜨린 호출부를 컴파일러가 잡는다.
 */
export const deriveAccountStatus = (
  user: AccountStatusSource,
  options: { readonly identityRequired: boolean },
): AccountStatus => {
  if (user.deletedAt) {
    return 'DELETED';
  }

  if (user.blockedAt) {
    return 'BLOCKED';
  }

  if (!user.emailVerifiedAt) {
    return 'PENDING_EMAIL';
  }

  // 관리자는 PASS 본인인증을 거치지 않으므로 면제한다. (설계 문서 §9-3)
  // 본인인증을 쓰지 않는 동안(IDENTITY_PROVIDER=disabled)은 이 단계가 없다.
  if (options.identityRequired && user.role !== 'ADMIN' && !user.identityVerifiedAt) {
    return 'PENDING_IDENTITY';
  }

  return 'ACTIVE';
};
